import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { TierBadge } from "@/components/TierBadge";
import { TrustChip, trustAttestationLine, DBS_JUDGEMENT_CLARIFIER } from "@/components/TrustChip";
import { useAuth } from "@/context/AuthContext";
import { ReportButton } from "@/components/ReportButton";
import { Seo } from "@/components/Seo";
import { TIERS, currentTier, progressToNext, earnedThemed } from "@/lib/badges";
import { Award, Trophy, MapPin, Clock, MessageSquare, Calendar, ThumbsUp, Plus, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import RatingSummary from "@/components/RatingSummary";
import { toast } from "sonner";

export default function PublicProfile() {
  const { userId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [isPrivate, setIsPrivate] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [counts, setCounts] = useState({});
  const [myEndorsed, setMyEndorsed] = useState([]);

  useEffect(() => {
    setNotFound(false);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  function load() {
    api.publicProfile(userId)
      .then((d) => { setData(d); setCounts(d.endorsements || {}); setMyEndorsed(d.my_endorsements || []); })
      .catch((e) => {
        setData(null);
        if (e?.response?.status === 403) setIsPrivate(true);
        else setNotFound(true);
      });
  }

  if (isPrivate) {
    return (
      <div data-testid="private-profile-notice" className="max-w-xl mx-auto px-6 py-24 text-center">
        <div className="font-display text-3xl text-forest">This profile is private</div>
        <p className="text-neutral-600 mt-3 text-sm">This volunteer has chosen to keep their profile, location and statistics private.</p>
      </div>
    );
  }
  if (notFound) {
    return (
      <div data-testid="profile-not-found" className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="font-display text-3xl text-forest">Profile not found</div>
        <p className="text-neutral-600 mt-3 text-sm">This profile may no longer exist, or the link is wrong.</p>
        <Link to="/" data-testid="profile-not-found-back" className="inline-block mt-6 text-forest font-medium underline">Back to CivicHearth</Link>
      </div>
    );
  }
  if (!data) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-500">Loading profile…</div>;

  const u = data.user;
  const s = data.stats;
  const isOrg = u.user_type === "organisation";
  const tier = !isOrg ? currentTier(s.total_hours) : null;
  const prog = !isOrg ? progressToNext(s.total_hours) : null;
  const themed = !isOrg ? earnedThemed({ total_hours: s.total_hours, logs_count: s.logs_count || 0, orgs_count: s.orgs_count || 0 }) : [];

  const shareUrl = window.location.href;
  const copy = () => navigator.clipboard.writeText(shareUrl);

  const canEndorse = user && user.user_id !== u.user_id;
  const endorse = async (skill) => {
    try {
      if (myEndorsed.includes(skill)) {
        // Toggle off — remove my endorsement and keep counts consistent.
        const res = await api.unendorseSkill(u.user_id, skill);
        setCounts(res.counts || counts);
        setMyEndorsed(list => list.filter(sk => sk !== skill));
        toast.message(`Endorsement removed`);
      } else {
        const res = await api.endorseSkill(u.user_id, skill);
        setCounts(res.counts || counts);
        setMyEndorsed(list => [...list, skill]);
        toast[res.already_endorsed ? "message" : "success"](res.already_endorsed ? "Already endorsed" : `Endorsed ${skill}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not endorse");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="public-profile-page">
      <Seo title={u.name} description={`${u.name}'s volunteer profile on CivicHearth — hours, badges and skill endorsements.`} />
      <Card className="border-neutral-200">
        <CardContent className="p-8">
          <div className="flex items-start gap-6 flex-wrap">
            <Avatar className="w-24 h-24 border border-forest/10">
              <AvatarImage src={u.picture} alt={u.name} />
              <AvatarFallback className="bg-forest text-white text-2xl">{u.name?.[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-[240px]">
              <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">{u.user_type || "member"}</div>
              <h1 className="font-display text-4xl text-forest tracking-tight mt-1">
                {isOrg ? (u.org_name || u.name) : u.name}
                {!isOrg && <TierBadge hours={s.total_hours} className="ml-3" />}
                {u.role_badge && <RoleBadge badge={u.role_badge} className="ml-2" />}
              </h1>
              {!isOrg && u.real_name && u.real_name !== u.name && (
                <div data-testid="profile-real-name" className="text-sm text-neutral-500 mt-0.5">{u.real_name}</div>
              )}
              {!isOrg && data.trust && (
                <div className="mt-2" data-testid="profile-trust-row">
                  <div className="flex items-center gap-2 flex-wrap">
                    <TrustChip trust={data.trust} showZero />
                    {data.trust.level >= 3 && data.trust.attestation && (
                      <span className="text-xs text-neutral-500" data-testid="profile-attestation-line">
                        {trustAttestationLine(data.trust.attestation)}
                      </span>
                    )}
                  </div>
                  {data.trust.level >= 3 && (
                    <p className="text-xs text-neutral-400 mt-1 max-w-md" data-testid="profile-dbs-clarifier">
                      {DBS_JUDGEMENT_CLARIFIER}
                    </p>
                  )}
                </div>
              )}
              {!isOrg && data.trust && user?.user_type === "organisation" && data.trust.viewer_can_attest && (
                <DbsAttestControl trust={data.trust} volunteerId={u.user_id} onChanged={load} />
              )}
              {u.location && <div className="text-sm text-neutral-600 mt-1 inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-forest/60" />{u.location}</div>}
              {u.bio && <p className="text-neutral-700 leading-relaxed mt-3 max-w-2xl">{u.bio}</p>}
              <div className="flex flex-wrap gap-1.5 mt-4">
                {u.values?.map(v => <Badge key={v} variant="outline" className="rounded-full text-xs capitalize border-forest/20 text-forest">{v}</Badge>)}
              </div>
              <div className="flex flex-wrap gap-2 mt-3" data-testid="profile-skills">
                {u.skills?.map(sk => (
                  <div key={sk} data-testid={`skill-${sk}`} className="inline-flex items-center gap-1.5 rounded-full bg-terracotta/10 text-terracotta text-xs pl-3 pr-1.5 py-1">
                    <span className="capitalize">{sk.replace(/-/g, " ")}</span>
                    {counts[sk] > 0 && <span className="inline-flex items-center gap-0.5 font-semibold"><ThumbsUp className="w-3 h-3" />{counts[sk]}</span>}
                    {canEndorse && (
                      <button
                        data-testid={`endorse-${sk}`}
                        onClick={() => endorse(sk)}
                        className={`ml-0.5 w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
                          myEndorsed.includes(sk)
                            ? "bg-terracotta text-white hover:bg-terracotta/70"
                            : "bg-terracotta/20 hover:bg-terracotta hover:text-white"
                        }`}
                        title={myEndorsed.includes(sk) ? `Remove your ${sk} endorsement` : `Endorse ${sk}`}
                      >
                        {myEndorsed.includes(sk) ? <ThumbsUp className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Button data-testid="copy-profile-link" variant="outline" onClick={copy} className="rounded-full">Share profile</Button>
              {user && user.user_id !== u.user_id && (
                <Link to={`/messages?to=${u.user_id}`}>
                  <Button className="rounded-full bg-forest hover:bg-forest-hover"><MessageSquare className="w-4 h-4 mr-1.5" />Message</Button>
                </Link>
              )}
              {user && user.user_id !== u.user_id && (
                <div className="self-center">
                  <ReportButton targetType="user" targetId={u.user_id} label="Report profile" />
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="mt-6">
        <RatingSummary userId={u.user_id} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-6">
        <div className="bg-white border border-neutral-200 rounded-xl p-6">
          <div className="text-xs uppercase tracking-[0.18em] text-neutral-500">{isOrg ? "Hours mobilised" : "Approved hours"}</div>
          <div className="font-display text-3xl text-forest mt-2">{s.total_hours}</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-xl p-6">
          <div className="text-xs uppercase tracking-[0.18em] text-neutral-500">{isOrg ? "Volunteers" : "Logs submitted"}</div>
          <div className="font-display text-3xl text-forest mt-2">{isOrg ? s.volunteers_count : s.logs_count}</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-xl p-6">
          <div className="text-xs uppercase tracking-[0.18em] text-neutral-500">{isOrg ? "Opportunities" : "Organisations"}</div>
          <div className="font-display text-3xl text-forest mt-2">{isOrg ? data.opportunities.length : s.orgs_count}</div>
        </div>
      </div>

      {!isOrg && (
        <>
          <Card className="mt-6 border-neutral-200">
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Tier</div>
                  <div className="font-display text-3xl text-forest mt-1">{tier ? tier.label : "Not yet ranked"}</div>
                  {prog.next && <div className="text-sm text-neutral-600 mt-1">{prog.next.threshold - s.total_hours}h to {prog.next.label}</div>}
                </div>
              </div>
              <Progress value={prog.pct} className="mt-5 h-2" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                {TIERS.map(t => {
                  const earned = s.total_hours >= t.threshold;
                  return (
                    <div key={t.id} className={`rounded-xl p-4 border ${earned ? "border-forest/30 bg-forest/5" : "border-neutral-200 bg-neutral-50 opacity-70"}`}>
                      <Trophy className="w-5 h-5" style={{ color: t.color }} />
                      <div className="font-display text-base text-forest mt-2">{t.label}</div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 mt-0.5">{t.threshold}h</div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <h2 className="font-display text-2xl text-forest mt-10 tracking-tight">Achievements</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {themed.filter(b => b.earned).map(b => (
              <div key={b.id} className="rounded-xl p-5 border border-terracotta/30 bg-white">
                <Award className="w-6 h-6 text-terracotta" />
                <div className="font-display text-lg text-forest mt-2">{b.label}</div>
                <div className="text-sm text-neutral-600 mt-1">{b.desc}</div>
              </div>
            ))}
            {themed.filter(b => b.earned).length === 0 && <div className="text-sm text-neutral-500">No achievements unlocked yet.</div>}
          </div>

          <h2 className="font-display text-2xl text-forest mt-10 tracking-tight">Recent contributions</h2>
          <div className="grid grid-cols-1 gap-3 mt-4">
            {data.recent_contributions.map((c) => (
              <div key={c.id} className="bg-white border border-neutral-200 rounded-xl p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-medium text-forest">{c.opportunity_title || c.org_name}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">{c.org_name} · {c.date ? new Date(c.date).toLocaleDateString() : ""}</div>
                  </div>
                  <div className="font-display text-xl text-forest">{c.hours}h</div>
                </div>
                {c.narrative && <div className="text-sm text-neutral-700 mt-2">{c.narrative}</div>}
              </div>
            ))}
            {data.recent_contributions.length === 0 && <div className="text-sm text-neutral-500">No public contributions yet.</div>}
          </div>
        </>
      )}

      {isOrg && (
        <Link to={`/o/${u.user_id}/impact`} className="block mt-6 bg-forest text-white rounded-xl p-5 text-sm hover:bg-forest-hover transition-colors" data-testid="profile-impact-link">
          View public impact report →
        </Link>
      )}
      {isOrg && (
        <>
          <h2 className="font-display text-2xl text-forest mt-10 tracking-tight">Open opportunities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {data.opportunities.map(o => (
              <Link key={o.id} to={`/opportunities/${o.id}`} className="bg-white border border-neutral-200 rounded-xl p-5 hover:-translate-y-0.5 transition-transform">
                <div className="font-medium text-forest">{o.title}</div>
                <div className="text-xs text-neutral-500 mt-0.5">{o.location}</div>
                <div className="flex gap-3 mt-3 text-sm text-neutral-600">
                  <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-forest/60" />{o.hours_required}h</span>
                  {o.event_date && (
                    <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4 text-forest/60" />{new Date(o.event_date).toLocaleDateString()}</span>
                  )}
                </div>
              </Link>
            ))}
            {data.opportunities.length === 0 && <div className="text-sm text-neutral-500">No active opportunities right now.</div>}
          </div>
        </>
      )}
    </div>
  );
}

// 18-B1 item 5: verified-org control — record or revoke "we have seen a
// current DBS certificate" for this volunteer. Date + org identity only;
// there is deliberately NO field for a certificate number or upload.
function DbsAttestControl({ trust, volunteerId, onChanged }) {
  const [seenOn, setSeenOn] = useState("");
  const [busy, setBusy] = useState(false);

  const record = async () => {
    if (!seenOn) return toast.error("Pick the date you saw the certificate");
    setBusy(true);
    try {
      await api.recordDbsAttestation({ volunteer_user_id: volunteerId, seen_on: seenOn });
      toast.success("Recorded — the volunteer's profile now shows \"DBS seen\"");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record"); }
    setBusy(false);
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.revokeDbsAttestation(trust.my_attestation_id);
      toast("Record removed");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke"); }
    setBusy(false);
  };

  return (
    <div className="mt-3 bg-neutral-50 border border-neutral-200 rounded-xl px-4 py-3 max-w-md" data-testid="dbs-attest-control">
      <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold inline-flex items-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5" /> DBS record
      </div>
      {trust.my_attestation_id ? (
        <div className="flex items-center justify-between gap-3 mt-2 flex-wrap">
          <span className="text-sm text-neutral-700">Your organisation has recorded seeing this volunteer's DBS certificate.</span>
          <Button data-testid="dbs-revoke-btn" onClick={revoke} disabled={busy} variant="outline" className="rounded-full h-8 px-3 text-xs text-red-600 border-red-200 hover:bg-red-50">
            Revoke
          </Button>
        </div>
      ) : (
        <div className="mt-2">
          <p className="text-xs text-neutral-500">
            Record that you've seen a current DBS certificate for this volunteer — just the date.
            No certificate details are ever stored, and you can remove the record at any time.
          </p>
          <div className="flex items-center gap-2 mt-2">
            <Input data-testid="dbs-seen-date" type="date" value={seenOn} onChange={(e) => setSeenOn(e.target.value)} className="h-9 w-40 text-sm" />
            <Button data-testid="dbs-record-btn" onClick={record} disabled={busy} className="rounded-full bg-forest hover:bg-forest-hover h-9 px-4 text-xs">
              Record DBS seen
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
